import { createSlice } from "@reduxjs/toolkit";
import {
  getInfluencerProposals,
  acceptProposal,
  rejectProposal,
  getActiveCollaborations,
  sendCollaborationProposal,
  getMerchantProposals,
  joinOpenCollaboration,
  sendInfluencerRequest,
  getMerchantIncomingRequests,
  respondToInfluencerRequest,
  getInfluencerSentRequests,
} from "./CollaborationThunk";
import { status } from "../../_constants/common";

const initialState = {
  proposals: [],
  proposal: {
    status: null,
    data: null,
    error: null,
  },
  merchantProposals: [],
  merchantProposalsLoading: false,
  merchantProposalsError: null,
  // Merchant incoming requests (from influencers)
  merchantIncomingRequests: [],
  merchantIncomingLoading: false,
  merchantIncomingError: null,
  // Influencer sent requests (to merchants)
  influencerSentRequests: [],
  influencerSentLoading: false,
  influencerSentError: null,
  // Accept/reject from merchant side
  merchantAcceptedCollaboration: null,
  activeCollaborations: [],
  influencerProfile: null, // Influencer profile data from active collaborations API
  categories: [], // Categories from active collaborations API
  acceptedCollaboration: null,
  joinedDeals: [], // Track joined deal IDs (changed from Set to Array for Redux serialization)
  joinCollabLoading: false,
  influencerRequestLoading: false, // Track influencer request to merchant loading
  isLoading: false,
  error: null,
  actionLoading: false,
};

const collaborationSlice = createSlice({
  name: "collaboration",
  initialState,
  reducers: {
    clearProposals: (state) => {
      state.proposals = [];
      state.error = null;
    },
    removeProposal: (state, action) => {
      state.proposals = state.proposals.filter(
        (proposal) => proposal.id !== action.payload
      );
    },
    clearAcceptedCollaboration: (state) => {
      state.acceptedCollaboration = null;
    },
    clearProposalState: (state) => {
      state.proposal = {
        status: null,
        data: null,
        error: null,
      };
    },
    clearMerchantAcceptedCollaboration: (state) => {
      state.merchantAcceptedCollaboration = null;
    },
  },
  extraReducers: (builder) => {
    // Get Influencer Proposals
    builder
      .addCase(getInfluencerProposals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInfluencerProposals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.proposals = action.payload?.data || [];
      })
      .addCase(getInfluencerProposals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to fetch proposals";
      });

    // Accept Proposal
    builder
      .addCase(acceptProposal.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(acceptProposal.fulfilled, (state, action) => {
        state.actionLoading = false;
        const proposalId = action.payload?.proposalId;
        state.proposals = state.proposals.filter((p) => p.id !== proposalId);
        // Store the accepted collaboration data with promo code
        state.acceptedCollaboration = action.payload?.collaboration || null;
      })
      .addCase(acceptProposal.rejected, (state) => {
        state.actionLoading = false;
      });

    // Reject Proposal
    builder
      .addCase(rejectProposal.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectProposal.fulfilled, (state, action) => {
        state.actionLoading = false;
        const proposalId = action.payload?.proposalId;
        state.proposals = state.proposals.filter((p) => p.id !== proposalId);
      })
      .addCase(rejectProposal.rejected, (state) => {
        state.actionLoading = false;
      });

    // Get Active Collaborations
    builder
      .addCase(getActiveCollaborations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getActiveCollaborations.fulfilled, (state, action) => {
        state.isLoading = false;
        // API now returns {influencers: {...}, categories: [], collaborations: [], total: number}
        const responseData = action.payload?.data || {};
        const collaborations = responseData.collaborations || [];

        // Store influencer profile data
        state.influencerProfile = responseData.influencers || null;

        // Store categories data
        state.categories = responseData.categories || [];

        // Store collaborations array
        state.activeCollaborations = collaborations;
      })
      .addCase(getActiveCollaborations.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error?.message || "Failed to fetch active collaborations";
      });

    // Send Collaboration Proposal
    builder
      .addCase(sendCollaborationProposal.pending, (state) => {
        state.proposal.status = status.IN_PROGRESS;
        state.proposal.error = null;
      })
      .addCase(sendCollaborationProposal.fulfilled, (state, action) => {
        state.proposal.status = status.SUCCESS;
        state.proposal.data = action.payload;
        state.proposal.error = null;
      })
      .addCase(sendCollaborationProposal.rejected, (state, action) => {
        state.proposal.status = status.FAILURE;
        state.proposal.error = action.payload;
      });

    // Get Merchant Proposals
    builder
      .addCase(getMerchantProposals.pending, (state) => {
        state.merchantProposalsLoading = true;
        state.merchantProposalsError = null;
      })
      .addCase(getMerchantProposals.fulfilled, (state, action) => {
        state.merchantProposalsLoading = false;
        state.merchantProposals = action.payload?.data || [];
      })
      .addCase(getMerchantProposals.rejected, (state, action) => {
        state.merchantProposalsLoading = false;
        state.merchantProposalsError =
          action.error?.message || "Failed to fetch proposals";
      });

    // Join Open Collaboration
    builder
      .addCase(joinOpenCollaboration.pending, (state) => {
        state.joinCollabLoading = true;
      })
      .addCase(joinOpenCollaboration.fulfilled, (state, action) => {
        state.joinCollabLoading = false;
        const dealId = action.payload?.dealId;
        if (dealId && !state.joinedDeals.includes(dealId)) {
          state.joinedDeals.push(dealId);
        }
      })
      .addCase(joinOpenCollaboration.rejected, (state) => {
        state.joinCollabLoading = false;
      });

    // Send Influencer Request to Merchant
    builder
      .addCase(sendInfluencerRequest.pending, (state) => {
        state.influencerRequestLoading = true;
      })
      .addCase(sendInfluencerRequest.fulfilled, (state, action) => {
        state.influencerRequestLoading = false;
        // Optionally store the request data
        // state.lastRequest = action.payload?.data;
      })
      .addCase(sendInfluencerRequest.rejected, (state) => {
        state.influencerRequestLoading = false;
      });

    // Get Merchant Incoming Requests (from influencers)
    builder
      .addCase(getMerchantIncomingRequests.pending, (state) => {
        state.merchantIncomingLoading = true;
        state.merchantIncomingError = null;
      })
      .addCase(getMerchantIncomingRequests.fulfilled, (state, action) => {
        state.merchantIncomingLoading = false;
        state.merchantIncomingRequests = action.payload?.data || [];
      })
      .addCase(getMerchantIncomingRequests.rejected, (state, action) => {
        state.merchantIncomingLoading = false;
        state.merchantIncomingError =
          action.error?.message || "Failed to fetch incoming requests";
      });

    // Respond to Influencer Request (merchant accept/reject)
    builder
      .addCase(respondToInfluencerRequest.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(respondToInfluencerRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        const requestId = action.payload?.requestId;
        const wasAccepted = action.payload?.accept;

        // Update the request in the list
        state.merchantIncomingRequests = state.merchantIncomingRequests.map((req) => {
          if (req.id === requestId) {
            return {
              ...req,
              isAccepted: wasAccepted,
              isRejected: !wasAccepted,
              rejectionReason: action.payload?.data?.rejectionReason || null,
            };
          }
          return req;
        });

        // Store accepted collaboration if accepted
        if (wasAccepted) {
          state.merchantAcceptedCollaboration = action.payload?.data || null;
        }
      })
      .addCase(respondToInfluencerRequest.rejected, (state) => {
        state.actionLoading = false;
      });

    // Get Influencer Sent Requests (to merchants)
    builder
      .addCase(getInfluencerSentRequests.pending, (state) => {
        state.influencerSentLoading = true;
        state.influencerSentError = null;
      })
      .addCase(getInfluencerSentRequests.fulfilled, (state, action) => {
        state.influencerSentLoading = false;
        state.influencerSentRequests = action.payload?.data || [];
      })
      .addCase(getInfluencerSentRequests.rejected, (state, action) => {
        state.influencerSentLoading = false;
        state.influencerSentError =
          action.error?.message || "Failed to fetch sent requests";
      });
  },
});

export const {
  clearProposals,
  removeProposal,
  clearAcceptedCollaboration,
  clearProposalState,
  clearMerchantAcceptedCollaboration,
} = collaborationSlice.actions;

export default collaborationSlice.reducer;
